import React, { FC, useState } from 'react';
import { useFormikContext } from 'formik';
import { LoadingPage } from 'Shared/components';
import {
  PaymentPlanType,
  PaymentDetailsType,
} from 'QuoteApp/services/endpoints/GetPaymentDetails';
import { PurchaseFormValuesType } from '../../types';
import styles from './styles.module.css';
import { useAppContext, usePurchaseContext } from 'QuoteApp/services/providers';
import { Button } from '@clearcover/atlas';
import { getOrderedPaymentFrequencies } from './utils';
import { PaymentFrequency } from './PaymentFrequency';

type PaymentFrequenciesProps = {
  paymentDetails: PaymentDetailsType;
};

export const PaymentFrequencies: FC<PaymentFrequenciesProps> = ({
  paymentDetails,
}) => {
  const { values, setFieldValue } = useFormikContext<PurchaseFormValuesType>();
  const currentPlan = paymentDetails.paymentPlans.find(
    pd => pd.identifier === values.paymentPlanId
  );
  const { paymentFrequencies } = paymentDetails;
  const paymentFrequenciesCount = paymentFrequencies.length;

  const {
    changeSelectedPaymentPlan,
    rateLoading,
    api,
    setDetails,
  } = useAppContext();

  const { setSignaturePacketSendAttempt } = usePurchaseContext();
  const [savingPaymentPreference, setSavingPaymentPreference] = useState(false);
  const [showMore, setShowMore] = useState(false);

  const savePaymentPreference = async (
    paymentPlan: PaymentPlanType | undefined
  ) => {
    if (!!paymentPlan) {
      setSavingPaymentPreference(true);
      const response = await api.saveAppData({
        data: {
          paymentPreferences: {
            method: paymentPlan?.paymentMethodIdentifier,
            frequency: paymentPlan?.paymentFrequencyIdentifier,
            paymentPlanEnum: paymentPlan?.paymentPlanEnum,
          },
        },
      });
      setSignaturePacketSendAttempt(null);
      setDetails(response.data);
      setSavingPaymentPreference(false);
    }
  };

  const toggleShowMore = () => {
    setShowMore(!showMore);
  };

  // do not show radio buttons if there is only one payment method
  const hasOnePaymentFrequency = paymentFrequenciesCount === 1;

  const { preferred, remaining } = getOrderedPaymentFrequencies(
    paymentFrequencies,
    paymentDetails.paymentPlans
  );

  const allPaymentFrequencies = [...preferred, ...remaining];
  const allPaymentsCount = preferred.length + remaining.length;
  const preferredPaymentsCount = preferred.length;

  return (
    <div
      className={styles.frequency}
      role="group"
      aria-labelledby="payment_frequency_header"
    >
      {rateLoading ? (
        <LoadingPage />
      ) : (
        <>
          {allPaymentFrequencies
            .slice(0, showMore ? allPaymentsCount : preferredPaymentsCount)
            .map((freq, freqIndex) => {
              return (
                <PaymentFrequency
                  key={freq.paymentPlanId}
                  isHidden={
                    freqIndex >= preferredPaymentsCount ? !showMore : false
                  }
                  isDisabled={savingPaymentPreference}
                  isOnlyPaymentFrequency={hasOnePaymentFrequency}
                  isSelected={currentPlan?.identifier === freq.paymentPlanId}
                  onChecked={paymentPlan => {
                    setFieldValue('paymentPlanId', paymentPlan?.identifier);
                    changeSelectedPaymentPlan(paymentPlan);
                    savePaymentPreference(paymentPlan);
                  }}
                  paymentFrequency={freq}
                  paymentPlans={paymentDetails.paymentPlans}
                />
              );
            })}
          {paymentFrequenciesCount > preferredPaymentsCount && (
            <div className={styles.borderContainer}>
              <div className={styles.showExtraPaymentWrapper}>
                <Button
                  link
                  size="md"
                  className={styles.cleanLinkButton}
                  onClick={toggleShowMore}
                >
                  {showMore
                    ? 'View less payment plans'
                    : 'View more payment plans'}
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
